import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";

export function InputField({ label, asterisk, children, className = "" }) {
  return (
    <div className={className}>
      <label className="label-eyebrow block mb-2">
        {label} {asterisk && <span className="text-volt">*</span>}
      </label>
      {children}
    </div>
  );
}

export function TextInput({ value, onChange, onBlur, placeholder, dataTestid, className = "" }) {
  return (
    <input
      data-testid={dataTestid}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      className={`w-full bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none ${className}`}
    />
  );
}

export function SelectInput({ value, onChange, children, className = "" }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`w-full bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none ${className}`}
    >
      {children}
    </select>
  );
}

export function TextArea({ value, onChange, rows = 4, placeholder, dataTestid, className = "" }) {
  return (
    <textarea
      data-testid={dataTestid}
      rows={rows}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none ${className}`}
    />
  );
}

export function SegmentedToggle({ options, value, onChange }) {
  return (
    <div className="inline-flex border border-border overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-5 py-[13px] text-xs tracking-widest uppercase font-bold transition-all ${
            value === opt.value
              ? "bg-volt text-primary-foreground"
              : "bg-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function PaymentMethodRow({ label, description, checked, onCheckedChange }) {
  return (
    <label className="flex items-center justify-between px-4 py-3 bg-card cursor-pointer hover:bg-muted transition-colors">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[10px] text-muted-foreground/80 tracking-wider mt-0.5">{description}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="data-[state=checked]:bg-volt data-[state=unchecked]:bg-border/30 [&>span]:bg-foreground"
      />
    </label>
  );
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const CLOSE_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);

export function WeeklyDayRow({ dayKey, day, entry, defaultOpen, defaultClose, onChange }) {
  const isClosed = entry?.is_closed ?? false;
  const openVal = entry?.open_hour ?? defaultOpen;
  const closeVal = entry?.close_hour ?? defaultClose;

  return (
    <div className="flex flex-wrap items-center gap-3 border border-border/30 bg-card px-4 py-3">
      <span className="w-20 text-[11px] tracking-widest uppercase font-bold text-muted-foreground">{dayKey}</span>
      <button
        type="button"
        onClick={() => onChange(day, { is_closed: !isClosed })}
        className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border transition-all ${
          isClosed ? "border-red-400 text-red-400" : "border-volt text-volt"
        }`}
      >
        {isClosed ? "Closed" : "Open"}
      </button>
      {!isClosed && (
        <>
          <select
            value={openVal}
            onChange={(e) => onChange(day, { open_hour: parseInt(e.target.value), is_closed: false })}
            className="bg-background border border-border px-3 py-1.5 text-xs focus:border-volt focus:outline-none"
          >
            {HOUR_OPTIONS.map((i) => (
              <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
            ))}
          </select>
          <span className="text-[10px] text-muted-foreground/80">—</span>
          <select
            value={closeVal}
            onChange={(e) => onChange(day, { close_hour: parseInt(e.target.value), is_closed: false })}
            className="bg-background border border-border px-3 py-1.5 text-xs focus:border-volt focus:outline-none"
          >
            {CLOSE_HOUR_OPTIONS.map((h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

export function ReviewRow({ label, value }) {
  return (
    <div>
      <span className="text-muted-foreground/80 block text-[10px] tracking-widest uppercase mb-0.5">{label}</span>
      <span className="text-foreground/90">{value || "—"}</span>
    </div>
  );
}

export function PeakHourGrid({ peakHours, togglePeak }) {
  return (
    <div className="grid grid-cols-8 sm:grid-cols-12 gap-1.5">
      {Array.from({ length: 24 }, (_, i) => i).map((h) => {
        const active = (peakHours || []).includes(h);
        return (
          <button
            key={h}
            type="button"
            onClick={() => togglePeak(h)}
            className={`py-1.5 text-[10px] font-mono border transition-all ${active
              ? "border-volt bg-volt text-primary-foreground font-bold"
              : "border-border text-muted-foreground/80 hover:border-foreground/30"
            }`}
          >
            {String(h).padStart(2, "0")}
          </button>
        );
      })}
    </div>
  );
}

export function ExtraItemEditor({ extra, index, extras, setExtras, t }) {
  const update = (field, value) => {
    const next = [...(extras || [])];
    next[index] = { ...next[index], [field]: value };
    setExtras(next);
  };

  return (
    <div className="border border-border/30 bg-card p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("owner.court_form.extra_name")}</label>
          <input
            value={extra.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={t("owner.court_form.extra_name_placeholder")}
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("owner.court_form.extra_price")}</label>
          <input
            type="number"
            min={0}
            step={0.5}
            value={extra.price}
            onChange={(e) => update("price", parseFloat(e.target.value) || 0)}
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("owner.court_form.extra_min_qty")}</label>
          <input
            type="number"
            min={0}
            value={extra.min_qty ?? 1}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0;
              update("min_qty", val);
              if (val > (extra.max_qty || 1)) update("max_qty", val);
            }}
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("owner.court_form.extra_max_qty")}</label>
          <input
            type="number"
            min={1}
            value={extra.max_qty ?? 1}
            onChange={(e) => update("max_qty", parseInt(e.target.value) || 1)}
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("owner.court_form.extra_description")}</label>
        <input
          value={extra.description}
          onChange={(e) => update("description", e.target.value)}
          placeholder={t("owner.court_form.extra_description_placeholder")}
          className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
        />
      </div>
      <button
        type="button"
        onClick={() => setExtras((extras || []).filter((_, j) => j !== index))}
        className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1 mt-3 hover:!border-red-400 hover:!text-red-400"
      >
        <X className="h-3 w-3" /> {t("owner.court_form.remove_extra")}
      </button>
    </div>
  );
}
